/**
 * WatchWidget -- "Watch Something" widget on the home dashboard.
 *
 * Shows a featured video recommendation and a horizontal scroll of
 * content cards. Widget #19 in the WidgetEngine.
 */

import type { Widget, WidgetContext } from '../types';
import { getFeaturedContent, CONTENT_CATALOG, SOURCE_COLORS, LANG_FLAGS } from '../../watch/ContentCatalog';
import type { ContentItem } from '../../watch/ContentCatalog';

function getRecommendation(locale: string): ContentItem {
  // Prefer content in user's language first
  const localeItems = CONTENT_CATALOG.filter((c) => c.featured && c.languages.includes(locale));
  if (localeItems.length > 0) {
    return localeItems[Math.floor(Math.random() * localeItems.length)];
  }
  const featured = getFeaturedContent();
  return featured[Math.floor(Math.random() * featured.length)] || CONTENT_CATALOG[0];
}

function getQuickPicks(locale: string): ContentItem[] {
  // 4 diverse picks: 1 documentary, 1 educational, 1 kids, 1 other
  const picks: ContentItem[] = [];
  const doc = CONTENT_CATALOG.find((c) => c.type === 'documentary' && c.languages.includes(locale) && !picks.includes(c));
  if (doc) picks.push(doc);
  const edu = CONTENT_CATALOG.find((c) => c.type === 'course' && c.languages.includes(locale) && !picks.includes(c));
  if (edu) picks.push(edu);
  const kids = CONTENT_CATALOG.find((c) => c.age_range === 'kids' && !picks.includes(c));
  if (kids) picks.push(kids);
  const live = CONTENT_CATALOG.find((c) => (c.type === 'live_tv' || c.type === 'live_radio') && !picks.includes(c));
  if (live) picks.push(live);

  // Fill remaining slots from featured
  const remaining = CONTENT_CATALOG.filter((c) => c.featured && !picks.includes(c));
  while (picks.length < 4 && remaining.length > 0) {
    picks.push(remaining.shift()!);
  }

  return picks.slice(0, 4);
}

export const WatchWidget: Widget = {
  id: 'watch-something',
  label: {
    en: 'Watch',
    fr: 'Regarder',
    ar: '\u0634\u0627\u0647\u062F',
    es: 'Ver',
    de: 'Ansehen',
  },
  icon: '\u25B6\uFE0F',
  defaultVisible: true,
  defaultOrder: 19,
  defaultSize: 'expanded',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    const locale = ctx.locale;
    const featured = getRecommendation(locale);
    const picks = getQuickPicks(locale);

    if (size === 'compact') {
      // Compact: single featured card
      const sourceColor = SOURCE_COLORS[featured.source] || 'var(--accent)';
      container.innerHTML = `
        <div class="rounded-2xl p-4 cursor-pointer flex items-center gap-3 transition-transform active:scale-[0.98]" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="watch" data-content-id="${featured.id}">
          <div style="width:48px;height:48px;border-radius:12px;overflow:hidden;flex-shrink:0;background:var(--bg-secondary);">
            ${featured.thumbnail
              ? `<img src="${featured.thumbnail}" alt="" style="width:100%;height:100%;object-fit:cover;" loading="lazy"/>`
              : `<div style="width:100%;height:100%;background:linear-gradient(135deg,${sourceColor}40,${sourceColor}20);display:flex;align-items:center;justify-content:center;">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="${sourceColor}"><polygon points="5,3 19,12 5,21"/></svg>
                </div>`
            }
          </div>
          <div style="flex:1;min-width:0;">
            <p class="text-xs" style="color:var(--text-secondary);">${ctx.t('widget_watch_title')}</p>
            <p class="text-sm font-medium truncate" style="color:var(--text-primary);">${_escapeHtml(featured.title[locale] || featured.title.en)}</p>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--accent)"><polygon points="5,3 19,12 5,21"/></svg>
        </div>
      `;
    } else {
      // Expanded: featured + horizontal scroll
      const sourceColor = SOURCE_COLORS[featured.source] || 'var(--accent)';
      container.innerHTML = `
        <div class="rounded-2xl overflow-hidden" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
          <!-- Featured -->
          <div class="cursor-pointer" style="position:relative;aspect-ratio:2/1;background:#000;" data-nav="watch" data-content-id="${featured.id}">
            ${featured.thumbnail
              ? `<img src="${featured.thumbnail}" alt="" style="width:100%;height:100%;object-fit:cover;opacity:0.75;" loading="lazy"/>`
              : `<div style="width:100%;height:100%;background:linear-gradient(135deg,${sourceColor},#3B82F6);"></div>`
            }
            <div style="position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,0.7) 0%,transparent 50%);"></div>
            <div style="position:absolute;bottom:0;left:0;right:0;padding:16px;">
              <span class="px-2 py-0.5 rounded-full text-xs font-bold" style="background:${sourceColor};color:white;">${featured.source}</span>
              <p class="text-sm font-bold mt-1 line-clamp-1" style="color:white;">${_escapeHtml(featured.title[locale] || featured.title.en)}</p>
            </div>
            <button style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:44px;height:44px;border-radius:50%;background:rgba(255,255,255,0.9);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 12px rgba(0,0,0,0.2);">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="var(--accent)"><polygon points="9,5 19,12 9,19"/></svg>
            </button>
          </div>

          <!-- Quick picks scroll -->
          <div style="padding:12px;">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
              <p class="text-xs font-bold uppercase tracking-wide" style="color:var(--text-secondary);">${ctx.t('widget_watch_title')}</p>
              <button class="text-xs font-medium" style="color:var(--accent);background:none;border:none;cursor:pointer;" data-nav="watch">${ctx.t('home_view_all')}</button>
            </div>
            <div style="display:flex;gap:10px;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;padding-bottom:2px;">
              ${picks.map((item) => {
                const sc = SOURCE_COLORS[item.source] || 'var(--accent)';
                const isLive = item.type === 'live_tv' || item.type === 'live_radio';
                return `
                  <div class="watch-pick cursor-pointer" data-nav="watch" data-content-id="${item.id}" style="min-width:140px;max-width:140px;flex-shrink:0;border-radius:12px;overflow:hidden;background:var(--bg-secondary);">
                    <div style="position:relative;aspect-ratio:16/9;">
                      ${item.thumbnail
                        ? `<img src="${item.thumbnail}" alt="" style="width:100%;height:100%;object-fit:cover;" loading="lazy"/>`
                        : `<div style="width:100%;height:100%;background:linear-gradient(135deg,${sc}40,${sc}20);display:flex;align-items:center;justify-content:center;">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="${sc}"><polygon points="5,3 19,12 5,21"/></svg>
                          </div>`
                      }
                      ${isLive ? `<span style="position:absolute;top:3px;left:3px;background:#EF4444;color:white;padding:1px 6px;border-radius:3px;font-size:8px;font-weight:700;">LIVE</span>` : ''}
                    </div>
                    <div style="padding:6px 8px;">
                      <p class="text-xs font-medium" style="color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${_escapeHtml(item.title[locale] || item.title.en)}</p>
                      <p class="text-xs" style="color:${sc};font-size:10px;">${item.source}</p>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        </div>
      `;
    }

    // Bind navigation -- tapping a card navigates to Watch module with content ID
    // NEVER open external links. All playback happens inside AWENI.
    container.querySelectorAll<HTMLElement>('[data-nav]').forEach((el) => {
      el.addEventListener('click', () => {
        // Navigate to the watch module; the WatchModule will handle playback in-app
        // Pass content ID via hash parameter for deep linking
        const contentId = el.dataset.contentId;
        if (contentId) {
          ctx.navigate('watch', `?play=${contentId}`);
        } else {
          ctx.navigate('watch');
        }
      });
    });
  },
};

function _escapeHtml(text: string): string {
  const d = document.createElement('div');
  d.textContent = text;
  return d.innerHTML;
}
